<template>
  <div
    ref="contentRef"
    class="mentionable-content"
    :class="contentClass"
    v-html="html"
  />
</template>

<script>
import tippy from 'tippy.js'
import { createApp, nextTick } from 'vue'

import MentionHoverCard from './MentionHoverCard.vue'

export default {
  name: 'MentionableContent',
  props: {
    html: {
      type: String,
      default: '',
    },
    /* mentionables default structure:
      [
        {
          id: number,
          mentionId: string, // e.g. "user-1"
          type?: 'user',
          fullName: string,
          initials?: string,
          email?: string,
          company?: string,
          userGroupName?: string,
        },
        ...
      ]
    */
    mentionables: {
      type: Array,
      default: () => [],
    },
    hoverCardComponent: {
      type: Object,
      default: () => MentionHoverCard,
    },
    contentClass: {
      type: [String, Array, Object],
      default: '',
    },
    mentionClass: {
      type: String,
      default: 'mentionable-content__mention',
    },
  },
  data () {
    return {
      mentionTippies: [],
    }
  },
  computed: {
    mentionablesById () {
      return this.mentionables.reduce((acc, item) => {
        if (item?.mentionId) {
          acc[item.mentionId] = item
        }
        return acc
      }, {})
    },
  },
  mounted () {
    this.bindMentionHoverCards()
  },
  beforeUnmount () {
    this.destroyMentionHoverCards()
  },
  methods: {
    destroyMentionHoverCards () {
      this.mentionTippies.forEach(({ instance, app }) => {
        instance?.destroy?.()
        app?.unmount?.()
      })
      this.mentionTippies = []
    },

    async bindMentionHoverCards () {
      this.destroyMentionHoverCards()

      await nextTick()

      const root = this.$refs.contentRef
      if (!root) return

      const mentionNodes = root.querySelectorAll(
        'span.mention[data-id], span[data-type="mention"][data-id], span[data-mention][data-id]',
      )

      mentionNodes.forEach((node) => {
        const mentionId = node.dataset.id
        const mentionable = this.mentionablesById[mentionId]

        node.classList.add(this.mentionClass)
        node.setAttribute('tabindex', '-1')

        if (!mentionable) return

        const mountNode = document.createElement('div')
        const app = createApp(this.hoverCardComponent, {
          mentionable,
        })
        app.mount(mountNode)

        const instance = tippy(node, {
          content: mountNode,
          allowHTML: true,
          appendTo: () => document.body,
          interactive: true,
          trigger: 'mouseenter',
          placement: 'top-start',
          offset: [0, 10],
          maxWidth: 'none',
          delay: [100, 80],
          arrow: false,
          theme: 'light-border',
        })

        this.mentionTippies.push({
          instance,
          app,
        })
      })
    },
  },
  watch: {
    html () {
      this.bindMentionHoverCards()
    },
    mentionables: {
      deep: true,
      handler () {
        this.bindMentionHoverCards()
      },
    },
  },
}
</script>

<style scoped lang="scss">
.mentionable-content {
  min-width: 0;
}

:deep(.mention),
:deep(span[data-type="mention"]),
:deep(span[data-mention]) {
  color: rgb(var(--v-theme-info-darken-1));
  font-weight: 400;
  cursor: pointer;
}
</style>
